import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api, { baseURL } from "../../../Axios";

export default function SidebarGalleryManagementPage() {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [newSection, setNewSection] = useState("");
  const [images, setImages] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [newImages, setNewImages] = useState(null);

  // Fetch all gallery sections
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/all`);
        setGalleryData(response.data);
        setLoading(false);
      } catch (err) {
        console.error(err);

        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Create a new gallery section
  const handleCreateSection = async () => {
    // if (!newSection.trim()) {
    //   toast.error("Section name cannot be empty!");
    //   return;
    // }

    try {
      const formData = new FormData();
      // formData.append("sectionName", newSection);
      if (images) {
        Array.from(images).forEach((image) => formData.append("images", image));
      }

      const response = await api.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/create`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setGalleryData([...galleryData, response.data]);
      // setNewSection("");
      setImages(null);
      toast.success("Gallery section created successfully!");
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to create gallery section"
      );
    }
  };

  // Delete a gallery section
  const handleDeleteSection = async (sectionId) => {
    try {
      await api.delete(`${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/delete/${sectionId}`);
      setGalleryData(
        galleryData.filter((section) => section._id !== sectionId)
      );
      toast.success("Gallery section deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to delete gallery section"
      );
    }
  };

  // Edit a gallery section
  const handleUpdateSection = async () => {
    if (!editSectionName.trim()) {
      toast.error("Section name cannot be empty!");
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/update/${editingSectionId}`,
        {
          sectionName: editSectionName,
        }
      );

      setGalleryData((prevData) =>
        prevData.map((section) =>
          section._id === editingSectionId
            ? { ...section, sectionName: response.data.sectionName }
            : section
        )
      );
      setEditingSectionId(null);
      setEditSectionName("");
      toast.success("Gallery section updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to update gallery section"
      );
    }
  };

  // Delete a specific image from a section
  const handleDeleteImage = async (sectionId, imagePath) => {
    try {
      await api.delete(`${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/delete/${sectionId}`, {
        data: { imagePath },
      });

      setGalleryData((prevData) =>
        prevData.map((section) =>
          section._id === sectionId
            ? {
              ...section,
              images: section.images.filter((img) => img !== imagePath),
            }
            : section
        )
      );
      toast.success("Image deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message);
    }
  };

  // Add new images to a section
  const handleAddImages = async (sectionId) => {
    if (!newImages) {
      toast.error("Please select images to add!");
      return;
    }

    try {
      const formData = new FormData();
      Array.from(newImages).forEach((image) =>
        formData.append("images", image)
      );

      const response = await api.put(
        `${process.env.REACT_APP_BASE_URL}/api/v1/sidebargallery/update/${sectionId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setGalleryData((prevData) =>
        prevData.map((section) =>
          section._id === sectionId ? response.data : section
        )
      );
      setNewImages(null);
      toast.success("Images added successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to add images");
    }
  };

  if (loading) return <div className="py-8 text-center">Loading...</div>;
  if (error) return <div className="text-center text-red-500">{error}</div>;

  return (
    <div className="container px-4 py-8 mx-auto">
      <h1 className="mb-8 text-3xl font-bold text-center">
        Sidebar Gallery Management
      </h1>

      {/* Create New Section */}
      <section className="p-4 mb-8 border border-gray-300 rounded-md">
        <h2 className="mb-4 text-xl font-bold text-teal-500">
          Create New Gallery Section
        </h2>
        {/* <input
          type="text"
          placeholder="Section Name"
          value={newSection}
          onChange={(e) => setNewSection(e.target.value)}
          className="w-full p-2 border rounded-md"
        /> */}
        <input
          type="file"
          multiple
          onChange={(e) => setImages(e.target.files)}
          className="w-full p-2 mt-4 border rounded-md"
        />
        <button
          onClick={handleCreateSection}
          className="w-full px-4 py-2 mt-4 text-white bg-green-500 rounded-md sm:w-auto"
        >
          Create Section
        </button>
      </section>

      {/* View and Manage Sections */}
      <section className="mb-8">
        <h2 className="mb-4 text-xl font-semibold">All Gallery Sections</h2>
        <div className="space-y-6">
          {galleryData.map((section) => (
            <motion.div
              key={section._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="p-6 bg-white border rounded-lg shadow-lg"
            >
              <div className="flex items-center justify-between">
                {editingSectionId === section._id ? (
                  <input
                    type="text"
                    value={editSectionName}
                    onChange={(e) => setEditSectionName(e.target.value)}
                    className="w-full p-2 border rounded-md sm:w-72"
                  />
                ) : (
                  <h3 className="text-xl font-semibold text-gray-800">
                    {/* {section.sectionName} */}
                  </h3>
                )}

                <div className="flex space-x-2">
                  {editingSectionId === section._id ? (
                    <button
                      onClick={handleUpdateSection}
                      className="px-4 py-2 text-white bg-blue-500 rounded-md"
                    >
                      Save
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingSectionId(section._id);
                        setEditSectionName(section.sectionName);
                      }}
                      className="px-4 py-2 text-white bg-yellow-500 rounded-md"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteSection(section._id)}
                    className="px-4 py-2 text-white bg-red-500 rounded-md"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Images */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                {section.images.map((image, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/api/uploads/sidegallery/${image}`}
                      alt={`image ${i}`}
                      className="object-fill w-full rounded-lg"
                    />
                    <div className="absolute inset-0 transition-opacity bg-black bg-opacity-50 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => handleDeleteImage(section._id, image)}
                        className="absolute p-2 text-white bg-red-500 rounded-full top-2 right-2"
                      >
                        X
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add New Images */}
              <div className="mt-4">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setNewImages(e.target.files)}
                  className="w-full p-2 border rounded-md"
                />
                <button
                  onClick={() => handleAddImages(section._id)}
                  className="w-full px-4 py-2 mt-4 text-white bg-teal-500 rounded-md sm:w-auto"
                >
                  Add Images
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <ToastContainer />
    </div>
  );
}
